// src/modules/deals/components/DealsApp.tsx
//
// «Deals» como app del sistema operativo Orion (Samuel, 2026-10-24: «ya
// prácticamente todo debe de tener el diseño del sistema operativo»), hermana
// de la app «Leads» (#237): barra propia con identidad, pestañas Pipeline ·
// Forecast, ficha lateral (en el celular, pantalla completa con regresar) y
// alta «Nuevo deal».
//
// 🔒 Datos: SOLO endpoints existentes de mcp_django apps/deals, org SIEMPRE del
//   JWT: stages (GET · POST · PATCH), deals (list · get · POST · PATCH · /stage),
//   forecast, follow-ups, contacts/contact. La vendedora recibe del SERVIDOR
//   solo sus deals (#513); aquí isAdmin solo decide qué controles se pintan
//   (configurar columnas), nunca qué datos llegan.
// 🔒 Dinero: los totales salen del forecast del servidor; el cliente no suma.

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { BarChart3, Columns3, KanbanSquare, Loader2, Plus, RefreshCw, ShieldAlert } from 'lucide-react';

import { useIsMobile } from '@/components/ui/useIsMobile';
import { useModules } from '@/context/ModulesContext';
import { dealsApi, DealsApiError } from '../api';
import { fmtMoney, readPref, sortStages, writePref } from '../model';
import type { Deal, DealStage, FollowUpRow, ForecastReport, ForecastStageBucket } from '../types';
import { DealForm } from './DealForm';
import { DealKanban } from './DealKanban';
import { DealSheet, Field } from './DealSheet';
import { ForecastPanel } from './ForecastPanel';
import { DealsGlyph } from './parts';
import { BoardEmpty, ColumnsDialog } from './StageSetup';
import './deals-app.css';

export type DealsView = 'kanban' | 'forecast' | 'create';
type Toast = { type: 'success' | 'error' | 'info'; title: string; message?: string };

const MOBILE_STAGE_KEY = 'orion.deals-app.mobile-stage';

function describe(e: unknown): string {
  if (e instanceof DealsApiError) return e.status === 403 ? 'denied' : e.message;
  if (e instanceof Error) return e.message;
  return 'Error desconocido';
}

export function DealsApp({ view }: { view: DealsView }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const { isAdmin, userRoles } = useModules();
  const isSeller = !isAdmin && !!userRoles?.some((r) => ['vendedor', 'socio', 'partner', 'asociado'].includes(r));

  const [stages, setStages] = useState<DealStage[] | null>(null);
  const [deals, setDeals] = useState<Deal[] | null>(null);
  const [followUps, setFollowUps] = useState<Record<string, FollowUpRow>>({});
  const [forecast, setForecast] = useState<ForecastReport | null>(null);
  const [forecastError, setForecastError] = useState<string | null>(null);
  const [forecastLoading, setForecastLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(() => new URLSearchParams(location.search).get('deal'));
  const [mobileStage, setMobileStage] = useState<number | null>(() => Number(readPref(MOBILE_STAGE_KEY)) || null);
  const [colsOpen, setColsOpen] = useState(false);
  const [lostAsk, setLostAsk] = useState<{ deal: Deal; stage: DealStage } | null>(null);
  const [toast, setToast] = useState<Toast | null>(null);
  const toastTimer = useRef<number | null>(null);

  const notify = useCallback((type: Toast['type'], title: string, message?: string) => {
    setToast({ type, title, message });
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), type === 'error' ? 6000 : 3200);
  }, []);

  const loadForecast = useCallback(async () => {
    setForecastLoading(true);
    setForecastError(null);
    try { setForecast(await dealsApi.getForecast()); } catch (e) { setForecastError(describe(e)); } finally { setForecastLoading(false); }
  }, []);

  const loadFollowUps = useCallback(async () => {
    try {
      const rows = await dealsApi.listFollowUps(500);
      setFollowUps(Object.fromEntries(rows.map((r) => [r.deal_id, r])));
    } catch { /* el semáforo es complementario: sin él la tarjeta no muestra próximo contacto */ }
  }, []);

  const loadBoard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [st, ds] = await Promise.all([dealsApi.listStages(), dealsApi.list()]);
      setStages(sortStages(st));
      setDeals(ds);
    } catch (e) { setError(describe(e)); } finally { setLoading(false); }
  }, []);

  const loadAll = useCallback(() => { void loadBoard(); void loadForecast(); void loadFollowUps(); }, [loadBoard, loadForecast, loadFollowUps]);
  useEffect(() => { loadAll(); }, [loadAll]);

  // ?deal=<id> mantiene la ficha enlazable.
  useEffect(() => {
    if (view !== 'kanban') return; // la ficha solo vive en el Pipeline
    const sp = new URLSearchParams(location.search);
    const cur = sp.get('deal');
    if ((selectedId || null) === (cur || null)) return;
    if (selectedId) sp.set('deal', selectedId); else sp.delete('deal');
    const qs = sp.toString();
    navigate({ pathname: location.pathname, search: qs ? `?${qs}` : '' }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, view]);

  const stageList = stages || [];
  const selected = useMemo(() => deals?.find((d) => d.id === selectedId) || null, [deals, selectedId]);
  const buckets = useMemo(() => Object.fromEntries((forecast?.by_stage || []).map((b) => [b.stage_id, b])) as Record<number, ForecastStageBucket>, [forecast]);
  const currency = useMemo(() => deals?.[0]?.currency || 'MXN', [deals]);
  const hiddenCount = useMemo(() => {
    if (!deals || !stages) return 0;
    const ids = new Set(stages.map((s) => s.id));
    return deals.filter((d) => !ids.has(d.stage_id)).length;
  }, [deals, stages]);

  const upsert = useCallback((d: Deal) => setDeals((cur) => (cur ? cur.map((x) => (x.id === d.id ? d : x)) : cur)), []);

  const doMove = useCallback(async (deal: Deal, stage: DealStage, lostReason = '') => {
    if (deal.stage_id === stage.id) return;
    const prev = deal;
    upsert({ ...deal, stage_id: stage.id, stage_slug: stage.slug, stage_name: stage.name, status: stage.kind });
    try {
      const upd = await dealsApi.moveStage(deal.id, { stage_id: stage.id, ...(lostReason ? { lost_reason: lostReason } : {}) });
      upsert(upd);
      notify('success', `Movido a ${stage.name}`, deal.title);
      void loadForecast();
      void loadFollowUps();
    } catch (e: any) {
      upsert(prev);
      notify('error', 'No se movió el deal', e?.message);
    }
  }, [upsert, notify, loadForecast, loadFollowUps]);

  const move = useCallback((deal: Deal, stage: DealStage) => {
    if (deal.stage_id === stage.id) return;
    if (stage.kind === 'lost') setLostAsk({ deal, stage });
    else void doMove(deal, stage);
  }, [doMove]);

  const goView = (v: DealsView) => navigate(`/m/deals/${v}`);
  const denied = error === 'denied';

  // ── Encabezado de la app ────────────────────────────────────
  const subtitle = loading && !deals
    ? 'Cargando…'
    : denied ? 'Sin acceso'
    : stages && stages.length === 0 ? 'Sin columnas configuradas'
    : `${(deals?.length ?? 0).toLocaleString('es-MX')} ${isSeller ? 'tuyos' : 'en la organización'}${forecast ? ` · ponderado ${fmtMoney(forecast.weighted_pipeline, currency)}` : ''}`;

  const tabBtn = (k: 'kanban' | 'forecast', label: string, Icon: typeof KanbanSquare) => (
    <button key={k} type="button" role="tab" aria-selected={view === k} onClick={() => goView(k)}
      className={`inline-flex h-9 items-center justify-center gap-1.5 rounded-[10px] px-3 text-[13px] font-medium transition-colors ${view === k ? 'bg-card text-foreground shadow-sm ring-1 ring-border' : 'text-muted-foreground hover:text-foreground'}`}>
      <Icon className="h-4 w-4" aria-hidden />{label}
    </button>
  );
  const actionBtn = 'inline-flex h-9 items-center gap-1.5 rounded-xl border border-border bg-card px-3 text-[13px] font-medium text-foreground hover:bg-muted disabled:opacity-50';
  const portalTarget = typeof document !== 'undefined' ? document.querySelector('.orion-shell') : null;

  // ── Cuerpo ─────────────────────────────────────────────────
  let body: ReactNode;
  if (denied) {
    body = (
      <div className="grid h-full place-items-center p-6 text-center">
        <div className="max-w-sm">
          <span className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-muted text-muted-foreground"><ShieldAlert className="h-6 w-6" aria-hidden /></span>
          <h2 className="mt-3 text-[16px] font-semibold">Tu rol no tiene acceso al pipeline</h2>
          <p className="mt-1 text-[13.5px] text-muted-foreground">Pide a administración que te dé permiso de ver deals.</p>
        </div>
      </div>
    );
  } else if (error) {
    body = (
      <div className="p-4">
        <p role="alert" className="rounded-xl border border-destructive/40 bg-destructive/10 px-3 py-2 text-[13px] text-destructive">
          No se pudo cargar el pipeline: {error}{' '}
          <button type="button" onClick={loadAll} className="font-semibold underline">Reintentar</button>
        </p>
      </div>
    );
  } else if (loading && !stages) {
    body = <p className="flex items-center gap-2 p-6 text-[13px] text-muted-foreground" role="status"><Loader2 className="h-4 w-4 animate-spin" aria-hidden />Cargando pipeline…</p>;
  } else if (view === 'forecast') {
    body = <div className="dl-scroll h-full overflow-y-auto"><ForecastPanel report={forecast} stages={stageList} currency={currency} loading={forecastLoading} error={forecastError === 'denied' ? 'Tu rol no tiene acceso' : forecastError} onRetry={loadForecast} /></div>;
  } else if (stageList.length === 0) {
    body = <BoardEmpty isAdmin={!!isAdmin} onReady={loadAll} onCustomize={() => setColsOpen(true)} notify={notify} />;
  } else if (view === 'create') {
    body = (
      <div className="dl-scroll h-full overflow-y-auto">
        <DealForm stages={stageList} isSeller={isSeller} onCancel={() => goView('kanban')}
          onCreated={(d) => {
            setDeals((cur) => [d, ...(cur || [])]);
            notify('success', 'Deal creado', d.title);
            void loadForecast(); void loadFollowUps();
            navigate(`/m/deals/kanban?deal=${encodeURIComponent(d.id)}`);
            setSelectedId(d.id);
            setMobileStage(d.stage_id);
          }} />
      </div>
    );
  } else {
    const noDeals = (deals?.length ?? 0) === 0;
    body = (
      <div className="flex h-full min-h-0 flex-col">
        {noDeals && (
          <div className="mx-3 mt-3 flex flex-col gap-2 rounded-xl border border-border bg-card px-4 py-3 sm:mx-4 sm:flex-row sm:items-center">
            <p className="min-w-0 flex-1 text-[13px] text-foreground">
              <strong className="font-semibold">{isSeller ? 'Aún no tienes deals.' : 'Aún no hay deals.'}</strong>{' '}
              <span className="text-muted-foreground">Se crean solos cuando un lead pasa a «Calificado» en Leads, o créalo tú.</span>
            </p>
            <button type="button" onClick={() => goView('create')} className="dl-btn-brand inline-flex h-10 shrink-0 items-center justify-center gap-1.5 rounded-xl px-4 text-[13.5px] font-semibold">
              <Plus className="h-4 w-4" aria-hidden />Nuevo deal
            </button>
          </div>
        )}
        <div className="min-h-0 flex-1">
          <DealKanban stages={stageList} deals={deals || []} followUps={followUps} buckets={buckets} forecastReady={!!forecast}
            currency={currency} activeId={selectedId} isMobile={isMobile} isSeller={isSeller}
            mobileStageId={mobileStage} onMobileStage={(id) => { setMobileStage(id); writePref(MOBILE_STAGE_KEY, String(id)); }}
            onOpen={(d) => setSelectedId(d.id)} onMove={move}
            emptyHint={noDeals ? 'Sin deals todavía.' : undefined}
            footnote={`Arrastra una tarjeta a otra etapa o usa «Mover a…». Montos de columna: forecast del servidor (solo abiertos).${hiddenCount ? ` · ${hiddenCount} deals están en columnas ocultas.` : ''}`} />
        </div>
      </div>
    );
  }

  const sheet = selected && view === 'kanban' && !denied && (
    <DealSheet deal={selected} stages={stageList} followUp={followUps[selected.id]} isMobile={isMobile} isSeller={isSeller}
      onClose={() => setSelectedId(null)} onChanged={(d) => { upsert(d); void loadForecast(); void loadFollowUps(); }} onMove={move} notify={notify} />
  );

  return (
    <div className="deals-app flex w-full flex-col text-foreground" style={{ height: isMobile ? 'calc(100dvh - 72px)' : 'calc(100dvh - 150px)', minHeight: 520 }}>
      <div className="orion-card flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
        <header className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2.5 sm:px-4">
          <div className="flex min-w-0 flex-1 items-center gap-2.5 sm:flex-none">
            <DealsGlyph />
            <div className="min-w-0">
              <h1 className="text-[16px] font-semibold leading-tight">Deals</h1>
              <p className="truncate text-[12px] tabular-nums text-muted-foreground">{subtitle}</p>
            </div>
          </div>
          <nav role="tablist" aria-label="Vistas de Deals" className={isMobile ? 'order-3 grid w-full grid-cols-2 gap-1 rounded-xl bg-muted p-1' : 'flex items-center gap-1 rounded-xl bg-muted p-1'}>
            {tabBtn('kanban', 'Pipeline', isMobile ? Columns3 : KanbanSquare)}
            {tabBtn('forecast', 'Forecast', BarChart3)}
          </nav>
          <div className="flex items-center gap-2 sm:ml-auto">
            <button type="button" onClick={loadAll} disabled={loading} className={actionBtn} aria-label="Actualizar" title="Actualizar">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden />
            </button>
            {isAdmin && stageList.length > 0 && !denied && (
              <button type="button" onClick={() => setColsOpen(true)} className={actionBtn} aria-label="Configurar columnas" title="Configurar columnas">
                <Columns3 className="h-4 w-4" aria-hidden /><span className="hidden lg:inline">Columnas</span>
              </button>
            )}
            <button type="button" onClick={() => goView('create')} disabled={denied || stageList.length === 0} aria-current={view === 'create' ? 'page' : undefined}
              className="dl-btn-brand inline-flex h-9 items-center gap-1.5 rounded-xl px-3 text-[13px] font-semibold" aria-label="Nuevo deal">
              <Plus className="h-4 w-4" aria-hidden /><span className={isMobile ? 'sr-only' : ''}>Nuevo deal</span>
            </button>
          </div>
        </header>

        {isMobile && sheet ? (
          <div className="min-h-0 flex-1 border-t border-border">{sheet}</div>
        ) : (
          <div className="flex min-h-0 flex-1 border-t border-border bg-background/40">
            <div className="min-h-0 min-w-0 flex-1">{body}</div>
            {!isMobile && sheet}
          </div>
        )}
      </div>

      {colsOpen && (
        <ColumnsDialog stages={stageList} onClose={() => setColsOpen(false)} onChanged={() => { void loadBoard(); void loadForecast(); }} notify={notify} portalTarget={portalTarget} />
      )}
      {lostAsk && (
        <LostDialog deal={lostAsk.deal} stage={lostAsk.stage} portalTarget={portalTarget}
          onCancel={() => setLostAsk(null)}
          onConfirm={(reason) => { const a = lostAsk; setLostAsk(null); void doMove(a.deal, a.stage, reason); }} />
      )}

      <div aria-live="polite" className="pointer-events-none fixed inset-x-0 bottom-4 z-50 flex justify-center px-4">
        {toast && (
          <div role="status" className={`pointer-events-auto max-w-md rounded-xl border px-4 py-2.5 text-[13.5px] shadow-lg ${toast.type === 'error' ? 'border-destructive/50 bg-card text-destructive' : 'border-border bg-card text-foreground'}`}>
            <strong className="font-semibold">{toast.title}</strong>{toast.message ? <span className="text-muted-foreground"> · {toast.message}</span> : null}
          </div>
        )}
      </div>
    </div>
  );
}

/** Mover a una etapa «perdido»: pide el motivo (opcional) en vez de window.prompt. */
function LostDialog({ deal, stage, onCancel, onConfirm, portalTarget }: {
  deal: Deal; stage: DealStage; onCancel: () => void; onConfirm: (reason: string) => void; portalTarget?: Element | null;
}) {
  const [reason, setReason] = useState('');
  const ref = useRef<HTMLTextAreaElement | null>(null);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    ref.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); onCancel(); } };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); prev?.focus?.(); };
  }, [onCancel]);
  return createPortal(
    <div className="deals-app fixed inset-0 z-[60] flex items-end justify-center bg-black/50 backdrop-blur-sm sm:items-center sm:p-4" onMouseDown={onCancel}>
      <div role="dialog" aria-modal="true" aria-labelledby="dl-lost-title" data-dl-modal onMouseDown={(e) => e.stopPropagation()}
        className="dl-in w-full max-w-md rounded-t-2xl border border-border bg-card p-5 text-card-foreground shadow-2xl sm:rounded-2xl">
        <h2 id="dl-lost-title" className="text-[16px] font-semibold">Mover a {stage.name}</h2>
        <p className="mt-1 text-[13px] text-muted-foreground">«{deal.title}» se cerrará como perdido.</p>
        <div className="mt-3">
          <Field label="Motivo (opcional)">
            <textarea ref={ref} className="dl-input" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Precio, eligió a otro proveedor, sin respuesta…" />
          </Field>
        </div>
        <div className="mt-4 flex flex-col-reverse gap-2 min-[420px]:flex-row min-[420px]:justify-end">
          <button type="button" onClick={onCancel} className="h-11 rounded-xl px-4 text-[14px] font-medium text-foreground hover:bg-muted">Cancelar</button>
          <button type="button" onClick={() => onConfirm(reason.trim())} className="dl-btn-brand inline-flex h-11 items-center justify-center rounded-xl px-5 text-[14px] font-semibold">Marcar como perdido</button>
        </div>
      </div>
    </div>,
    portalTarget || document.body,
  );
}
